import {
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    fetch as fetchOne,
    fetchRecords,
    update,
} from '../services/TopLevelDomainsService';

import {TopLevelDomain} from '../types';

// FNP-944: records are React Query's job. Selection, paging and keyword stay in
// Redux. The service layer is unchanged - these hooks call the same service
// functions the Redux thunks called.

export const topLevelDomainsKeys = {
    all: ['top-level-domains'] as const,
    lists: () => [...topLevelDomainsKeys.all, 'list'] as const,
    list: (filter: object) => [...topLevelDomainsKeys.lists(), filter] as const,
    details: () => [...topLevelDomainsKeys.all, 'detail'] as const,
    detail: (id: string) => [...topLevelDomainsKeys.details(), id] as const,
};

export const useTopLevelDomainsQuery = (filter: object = {}) => {
    return useQuery({
        queryKey: topLevelDomainsKeys.list(filter),
        queryFn: async (): Promise<TopLevelDomain[]> => {
            const records = await fetchRecords(filter);
            return records ?? [];
        },
    });
};

export const useTopLevelDomainQuery = (topLevelDomainId?: string) => {
    return useQuery({
        queryKey: topLevelDomainsKeys.detail(topLevelDomainId ?? ''),
        queryFn: async () => {
            if (typeof topLevelDomainId === 'undefined') {
                throw new Error('topLevelDomainId is required parameter');
            }
            return await fetchOne(topLevelDomainId);
        },
        enabled: typeof topLevelDomainId !== 'undefined' && topLevelDomainId !== '',
    });
};

export const useCreateTopLevelDomainMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (values: TopLevelDomain) => add(values),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: topLevelDomainsKeys.lists()});
        },
    });
};

export const useUpdateTopLevelDomainMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
                         id,
                         record
                     }: { id: string, record: TopLevelDomain }) => update(id, record),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({queryKey: topLevelDomainsKeys.lists()});
            queryClient.invalidateQueries({queryKey: topLevelDomainsKeys.detail(variables.id)});
        },
    });
};

export const useDeleteTopLevelDomainMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteRecord(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: topLevelDomainsKeys.lists()});
        },
    });
};
